import React from "react";
import { FeatherAlertTriangle } from "@subframe/core";
import { useRevenueConcentration } from "../../hooks/useRevenueConcentration";

export interface RevenueConcentrationRowItem {
  color: string;
  name: string;
  amount: string;
  percent: string;
  showConcentrationBadge?: boolean;
}

export interface RevenueConcentrationSectionProps {
  title?: string;
  subtext?: string;
  monthButtonLabel?: string;
  onMonthClick?: () => void;
  totalRevenue?: string;
  totalRevenueLabel?: string;
  netInvoicedRevenue?: string;
  netInvoicedRevenueLabel?: string;
  barSegments?: { widthPercent: number; color: string; tooltip?: string }[];
  rows?: RevenueConcentrationRowItem[];
  noteText?: string;
}

export function RevenueConcentrationSection(
  props?: RevenueConcentrationSectionProps,
) {
  const data = useRevenueConcentration();

  const title = data.title;
  const subtext = data.subtext;
  const monthForUi =
    subtext.match(/month of\s+(.+)$/i)?.[1]?.trim() ||
    subtext.replace(/^Top Customer for the month of\s+/i, "").trim() ||
    subtext;

  const totalRevenue = data.totalRevenue;
  const totalRevenueLabel = data.totalRevenueLabel;
  const netInvoicedRevenue = data.netInvoicedRevenue;
  const netInvoicedRevenueLabel = data.netInvoicedRevenueLabel;
  const unbilledRevenue = data.unbilledRevenue;
  const unbilledRevenueLabel = data.unbilledRevenueLabel;
  const barSegments = data.barSegments;
  const rows = data.rows;
  const noteText = data.noteText;
  const loading = "loading" in data ? data.loading : false;
  const error = "error" in data ? data.error : null;

  const safeRows = Array.isArray(rows) ? rows : [];
  const safeBarSegments = Array.isArray(barSegments) ? barSegments : [];
  const hasAnyBreakdown = safeRows.length > 0 || safeBarSegments.length > 0;

  if (loading) {
    return (
      <div className="flex h-full min-h-0 w-full min-w-0 flex-col items-stretch justify-start gap-5 rounded-lg border border-solid border-neutral-200 bg-white px-6 py-6 shadow-sm">
        <div className="flex w-full flex-col items-start gap-0.5">
          <span className="text-heading-3 font-heading-3 text-neutral-900">
            {title}
          </span>
          <span className="text-caption font-caption text-neutral-500">
            {subtext}
          </span>
        </div>
        <div className="h-[36px] w-48 animate-pulse rounded bg-neutral-100" />
        <div className="h-3 w-full animate-pulse rounded-full bg-neutral-100" />
        <div className="flex w-full flex-col gap-2">
          {[1, 2, 3, 4, 5].map((i) => (
            <div
              key={i}
              className="h-10 w-full animate-pulse rounded bg-neutral-100"
            />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-full min-h-0 w-full min-w-0 flex-col items-stretch justify-start gap-5 rounded-lg border border-solid border-neutral-200 bg-white px-6 py-6 shadow-sm">
        <div className="flex w-full flex-col items-start gap-0.5">
          <span className="text-heading-3 font-heading-3 text-neutral-900">
            {title}
          </span>
          <span className="text-caption font-caption text-neutral-500">
            {subtext}
          </span>
        </div>
        <p className="text-body font-body text-neutral-500">{error}</p>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col items-stretch justify-start gap-5 rounded-lg border border-solid border-neutral-200 bg-white px-6 py-6 shadow-sm">
      <header className="flex w-full flex-col items-start gap-0.5">
        <h2 className="text-heading-3 font-heading-3 text-neutral-900">
          {title}
        </h2>
      </header>

      {!hasAnyBreakdown ? (
        <div className="flex flex-1 items-center justify-center">
          <div className="flex w-full max-w-[520px] flex-col gap-2 rounded-xl border border-neutral-200 bg-neutral-50 px-5 py-5 text-center">
            <p className="text-body font-body text-neutral-700">
              No revenue concentration data available for this period.
            </p>
            <p className="text-caption font-caption text-neutral-500">
              Once invoices or journals are recorded, this section will show the
              breakdown and top invoiced customers.
            </p>
          </div>
        </div>
      ) : (
        <>
          <div className="flex w-full flex-col">
            <div className="flex w-full flex-col gap-5 rounded-xl border border-neutral-200/90 bg-neutral-50/40 px-5 py-5">
              <div className="flex min-w-0 flex-col gap-1.5">
                <p className="font-['Inter'] text-[32px] font-bold leading-none tracking-tight text-neutral-900 sm:text-[36px]">
                  {totalRevenue}
                </p>
                <p className="text-caption font-caption leading-snug text-neutral-500">
                  {totalRevenueLabel}
                </p>
              </div>

              <div className="grid w-full grid-cols-1 gap-3 border-t border-neutral-200/80 pt-5 sm:grid-cols-2 sm:gap-4">
                <div className="flex min-w-0 flex-col gap-1 rounded-lg border border-neutral-100 bg-white px-3.5 py-3">
                  <span className="text-caption font-caption text-neutral-500">
                    {netInvoicedRevenueLabel}
                  </span>
                  <span className="font-['Inter'] text-[17px] font-semibold leading-snug text-neutral-900 tabular-nums">
                    {netInvoicedRevenue}
                  </span>
                </div>
                <div className="flex min-w-0 flex-col gap-1 rounded-lg border border-neutral-100 bg-white px-3.5 py-3">
                  <span className="text-caption font-caption text-neutral-500">
                    {unbilledRevenueLabel}
                  </span>
                  <span className="font-['Inter'] text-[17px] font-semibold leading-snug text-neutral-900 tabular-nums">
                    {unbilledRevenue}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex h-3 w-full flex-none items-center overflow-hidden rounded-full">
            {safeBarSegments.map((seg, i) => (
              <div
                key={i}
                className="flex flex-col items-start self-stretch border-r border-solid border-white"
                style={{
                  width: `${seg.widthPercent}%`,
                  backgroundColor: seg.color,
                }}
                title={seg.tooltip}
              />
            ))}
          </div>
          <p className="font-['Inter'] text-[15px] font-extrabold tracking-wide text-neutral-900">
            {`Top Invoiced Customers for the month of ${monthForUi}`}
          </p>
          <div className="flex w-full flex-col items-start">
            {safeRows.map((row, i) => (
              <div
                key={i}
                className={`flex w-full items-start gap-3 ${
                  i < safeRows.length - 1
                    ? "border-b border-solid border-neutral-100 py-3.5"
                    : "py-3.5"
                }`}
              >
                <div
                  className="mt-1 flex h-3 w-3 shrink-0 rounded-full"
                  style={{ backgroundColor: row.color }}
                />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                    <span className="text-body font-body text-neutral-900">
                      {row.name}
                    </span>
                    {row.showConcentrationBadge && (
                      <div className="inline-flex items-center gap-1 rounded-md border border-solid border-brand-100 bg-brand-50 px-2 py-0.5">
                        <FeatherAlertTriangle className="h-3 w-3 shrink-0 text-brand-700" />
                        <span className="font-['Inter'] text-[10px] font-bold uppercase leading-none text-brand-700">
                          Concentration
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex shrink-0 items-baseline justify-end gap-3 text-right sm:gap-4">
                  <span className="min-w-[4.5rem] text-body font-body text-neutral-500 tabular-nums sm:min-w-[5.5rem]">
                    {row.amount}
                  </span>
                  <span
                    className="min-w-[2.75rem] text-body-bold font-body-bold tabular-nums sm:min-w-12"
                    style={{ color: row.color }}
                  >
                    {row.percent}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {noteText && (
        <div className="flex w-full items-center gap-2 rounded-lg border border-solid border-[#bae6fd] bg-[#f0f9ff] px-4 py-4">
          <span className="text-body font-body text-[#0369a1]">{noteText}</span>
        </div>
      )}
    </div>
  );
}
